import { contains, getDocument, isMouseLikePointerType } from "./utils";
import { isElement } from "@floating-ui/utils/dom";
import * as React from "react";
import useModernLayoutEffect from "use-isomorphic-layout-effect";
import { useFloatingParentNodeId, useFloatingTree } from "@floating-ui/react";
import { createAttribute } from "./utils";
import { clearTimeoutIfSet } from "./utils";
import { useLatestRef } from "./utils";
import { useEffectEvent } from "./utils";
const safePolygonIdentifier = createAttribute("safe-polygon");
export function getDelay(value, prop, pointerType) {
    if (pointerType && !isMouseLikePointerType(pointerType)) {
        return 0;
    }
    if (typeof value === "number") {
        return value;
    }
    if (typeof value === "function") {
        const result = value();
        if (typeof result === "number") {
            return result;
        }
        return result === null || result === void 0 ? void 0 : result[prop];
    }
    return value === null || value === void 0 ? void 0 : value[prop];
}
function getRestMs(value) {
    if (typeof value === "function") {
        return value();
    }
    return value;
}
/**
 * Opens the floating element while hovering over the reference element, like
 * CSS `:hover`.
 * @see https://floating-ui.com/docs/useHover
 */
export function useHoverLeave(context, props = {}) {
    const { open, onOpenChange, dataRef, events, elements } = context;
    const { enabled = true, delay = 0, handleClose = null, mouseOnly = false, restMs = 0, move = true, } = props;
    const tree = useFloatingTree();
    const parentId = useFloatingParentNodeId();
    const handleCloseRef = useLatestRef(handleClose);
    const delayRef = useLatestRef(delay);
    const openRef = useLatestRef(open);
    const restMsRef = useLatestRef(restMs);
    const pointerTypeRef = React.useRef();
    const timeoutRef = React.useRef(-1);
    const handlerRef = React.useRef();
    const restTimeoutRef = React.useRef(-1);
    const blockMouseMoveRef = React.useRef(true);
    const performedPointerEventsMutationRef = React.useRef(false);
    const unbindMouseMoveRef = React.useRef(() => { });
    const restTimeoutPendingRef = React.useRef(false);
    const isHoverOpen = React.useCallback(() => {
        var _a;
        const type = (_a = dataRef.current.openEvent) === null || _a === void 0 ? void 0 : _a.type;
        return (type === null || type === void 0 ? void 0 : type.includes("mouse")) && type !== "mousedown";
    }, [dataRef]);
    // When closing before opening, clear the delay timeouts to cancel it
    // from showing.
    React.useEffect(() => {
        if (!enabled)
            return;
        function onOpenChange({ open }) {
            if (!open) {
                clearTimeoutIfSet(timeoutRef);
                clearTimeoutIfSet(restTimeoutRef);
                blockMouseMoveRef.current = true;
                restTimeoutPendingRef.current = false;
            }
        }
        events.on("openchange", onOpenChange);
        return () => {
            events.off("openchange", onOpenChange);
        };
    }, [enabled, events]);
    React.useEffect(() => {
        if (!enabled)
            return;
        if (!handleCloseRef.current)
            return;
        if (!open)
            return;
        function onLeave(event) {
            onOpenChange(false, event, "hover");
        }
        const html = getDocument(elements.floating).documentElement;
        html.addEventListener("mouseleave", onLeave);
        return () => {
            html.removeEventListener("mouseleave", onLeave);
        };
    }, [
        elements.floating,
        open,
        onOpenChange,
        enabled,
        handleCloseRef,
        isHoverOpen,
    ]);
    const closeWithDelay = React.useCallback((event, runElseBranch = true, reason = "hover") => {
        const closeDelay = getDelay(delayRef.current, "close", pointerTypeRef.current);
        if (closeDelay && !handlerRef.current) {
            clearTimeoutIfSet(timeoutRef);
            timeoutRef.current = window.setTimeout(() => onOpenChange(false, event, reason), closeDelay);
        }
        else if (runElseBranch) {
            clearTimeoutIfSet(timeoutRef);
            onOpenChange(false, event, reason);
        }
    }, [delayRef, onOpenChange]);
    const cleanupMouseMoveHandler = useEffectEvent(() => {
        unbindMouseMoveRef.current();
        handlerRef.current = undefined;
    });
    const clearPointerEvents = useEffectEvent(() => {
        if (performedPointerEventsMutationRef.current) {
            const body = getDocument(elements.floating).body;
            body.style.pointerEvents = "";
            body.removeAttribute(safePolygonIdentifier);
            performedPointerEventsMutationRef.current = false;
        }
    });
    // Registering the mouse events on the reference directly to bypass React's
    // delegation system. If the cursor was on a disabled element and then entered
    // the reference (no gap), `mouseenter` doesn't fire in the delegation system.
    React.useEffect(() => {
        if (!enabled)
            return;
        function onReferenceMouseLeave(event) {
            unbindMouseMoveRef.current();
            const doc = getDocument(elements.floating);
            clearTimeoutIfSet(restTimeoutRef);
            restTimeoutPendingRef.current = false;
            if (handleCloseRef.current && dataRef.current.floatingContext) {
                // Prevent clearing `onScrollMouseLeave` timeout.
                if (!open) {
                    clearTimeoutIfSet(timeoutRef);
                }
                handlerRef.current = handleCloseRef.current(Object.assign(Object.assign({}, dataRef.current.floatingContext), { tree, x: event.clientX, y: event.clientY, onClose() {
                        clearPointerEvents();
                        cleanupMouseMoveHandler();
                        closeWithDelay(event, true, "safe-polygon");
                    } }));
                const handler = handlerRef.current;
                doc.addEventListener("mousemove", handler);
                unbindMouseMoveRef.current = () => {
                    doc.removeEventListener("mousemove", handler);
                };
                return;
            }
            // Allow interactivity without `safePolygon` on touch devices. With a
            // pointer, a short close delay is an alternative, so it should work
            // consistently.
            const shouldClose = pointerTypeRef.current === "touch"
                ? !contains(elements.floating, event.relatedTarget)
                : true;
            if (shouldClose) {
                closeWithDelay(event);
            }
        }
        // Ensure the floating element closes after scrolling even if the pointer
        // did not move.
        // https://github.com/floating-ui/floating-ui/discussions/1692
        function onScrollMouseLeave(event) {
            var _a;
            if (!dataRef.current.floatingContext)
                return;
            (_a = handleCloseRef.current) === null || _a === void 0 ? void 0 : _a.call(handleCloseRef, Object.assign(Object.assign({}, dataRef.current.floatingContext), { tree, x: event.clientX, y: event.clientY, onClose() {
                    clearPointerEvents();
                    cleanupMouseMoveHandler();
                    closeWithDelay(event);
                } }))(event);
        }
        function onFloatingMouseEnter() {
            clearTimeoutIfSet(timeoutRef);
        }
        function onFloatingMouseLeave(event) {
            closeWithDelay(event, false);
        }
        if (isElement(elements.domReference)) {
            const reference = elements.domReference;
            const floating = elements.floating;
            if (open) {
                reference.addEventListener("mouseleave", onScrollMouseLeave);
            }
            //   if (move) {
            //     reference.addEventListener("mousemove", onReferenceMouseEnter, {
            //       once: true,
            //     });
            //   }
            //   reference.addEventListener("mouseenter", onReferenceMouseEnter);
            reference.addEventListener("mouseleave", onReferenceMouseLeave);
            if (floating) {
                floating.addEventListener("mouseleave", onScrollMouseLeave);
                floating.addEventListener("mouseenter", onFloatingMouseEnter);
                floating.addEventListener("mouseleave", onFloatingMouseLeave);
            }
            return () => {
                if (open) {
                }
                reference.removeEventListener("mouseleave", onScrollMouseLeave);
                // if (move) {
                //   reference.removeEventListener("mousemove", onReferenceMouseEnter);
                // }
                // reference.removeEventListener("mouseenter", onReferenceMouseEnter);
                reference.removeEventListener("mouseleave", onReferenceMouseLeave);
                if (floating) {
                    floating.removeEventListener("mouseleave", onScrollMouseLeave);
                    floating.removeEventListener("mouseenter", onFloatingMouseEnter);
                    floating.removeEventListener("mouseleave", onFloatingMouseLeave);
                }
            };
        }
    }, [
        elements,
        enabled,
        context,
        mouseOnly,
        move,
        closeWithDelay,
        cleanupMouseMoveHandler,
        clearPointerEvents,
        onOpenChange,
        open,
        openRef,
        tree,
        delayRef,
        handleCloseRef,
        dataRef,
        restMsRef,
    ]);
    // Block pointer-events of every element other than the reference and floating
    // while the floating element is open and has a `handleClose` handler. Also
    // handles nested floating elements.
    // https://github.com/floating-ui/floating-ui/issues/1722
    useModernLayoutEffect(() => {
        var _a, _b, _c;
        if (!enabled)
            return;
        if (open &&
            ((_a = handleCloseRef.current) === null || _a === void 0 ? void 0 : _a.__options.blockPointerEvents) &&
            isHoverOpen()) {
            performedPointerEventsMutationRef.current = true;
            const floatingEl = elements.floating;
            if (isElement(elements.domReference) && floatingEl) {
                const body = getDocument(elements.floating).body;
                body.setAttribute(safePolygonIdentifier, "");
                const ref = elements.domReference;
                const parentFloating = (_c = (_b = tree === null || tree === void 0 ? void 0 : tree.nodesRef.current.find((node) => node.id === parentId)) === null || _b === void 0 ? void 0 : _b.context) === null || _c === void 0 ? void 0 : _c.elements.floating;
                if (parentFloating) {
                    parentFloating.style.pointerEvents = "";
                }
                body.style.pointerEvents = "none";
                ref.style.pointerEvents = "auto";
                floatingEl.style.pointerEvents = "auto";
                return () => {
                    body.style.pointerEvents = "";
                    ref.style.pointerEvents = "";
                    floatingEl.style.pointerEvents = "";
                };
            }
        }
    }, [enabled, open, parentId, elements, tree, handleCloseRef, isHoverOpen]);
    useModernLayoutEffect(() => {
        if (!open) {
            pointerTypeRef.current = undefined;
            restTimeoutPendingRef.current = false;
            cleanupMouseMoveHandler();
            clearPointerEvents();
        }
    }, [open, cleanupMouseMoveHandler, clearPointerEvents]);
    React.useEffect(() => {
        return () => {
            cleanupMouseMoveHandler();
            clearTimeoutIfSet(timeoutRef);
            clearTimeoutIfSet(restTimeoutRef);
            clearPointerEvents();
        };
    }, [
        enabled,
        elements.domReference,
        cleanupMouseMoveHandler,
        clearPointerEvents,
    ]);
    const reference = React.useMemo(() => {
        function setPointerRef(event) {
            pointerTypeRef.current = event.pointerType;
        }
        return {
            onPointerDown: setPointerRef,
            onPointerEnter: setPointerRef,
            onMouseMove(event) {
                const { nativeEvent } = event;
                function handleMouseMove() {
                    if (!blockMouseMoveRef.current && !openRef.current) {
                        onOpenChange(true, nativeEvent, "hover");
                    }
                }
                if (mouseOnly && !isMouseLikePointerType(pointerTypeRef.current)) {
                    return;
                }
                if (open || getRestMs(restMsRef.current) === 0) {
                    return;
                }
                // Ignore insignificant movements to account for tremors.
                if (restTimeoutPendingRef.current &&
                    Math.pow(event.movementX, 2) + Math.pow(event.movementY, 2) < 2) {
                    return;
                }
                clearTimeoutIfSet(restTimeoutRef);
                if (pointerTypeRef.current === "touch") {
                    handleMouseMove();
                }
                else {
                    restTimeoutPendingRef.current = true;
                    restTimeoutRef.current = window.setTimeout(handleMouseMove, getRestMs(restMsRef.current));
                }
            },
        };
    }, [mouseOnly, onOpenChange, open, openRef, restMsRef]);
    return React.useMemo(() => (enabled ? { reference } : {}), [enabled, reference]);
}
